import React from "react";
import { Box, keyframes, alpha } from "@mui/material";
import {
  InsertDriveFileOutlined,
  DescriptionOutlined,
  FolderOpenOutlined,
  AssignmentOutlined,
  ArticleOutlined,
  EventNoteOutlined,
  ScienceOutlined,
  MedicalInformationOutlined,
} from "@mui/icons-material";
import { COLORS } from "../../../styles/uiConstants";

const float = keyframes`
  0%, 100% { transform: translate(-50%, -50%) translateY(0); }
  50% { transform: translate(-50%, -50%) translateY(-4px); }
`;

/**
 * Halkalar üzerinde yüzen dosya kutucukları — boş durum illüstrasyonu.
 * Konumlar merkeze göre (x, y) px; ölçek `size` ile çarpılır. Dış halkadakiler
 * daha soluk: derinlik hissi.
 */
const TILES = [
  { x: -96, y: -58, Icon: DescriptionOutlined, ring: 3 },
  { x: -34, y: -82, Icon: FolderOpenOutlined, ring: 2 },
  { x: 58, y: -66, Icon: AssignmentOutlined, ring: 2 },
  { x: 128, y: -84, Icon: InsertDriveFileOutlined, ring: 3 },
  { x: 116, y: -6, Icon: EventNoteOutlined, ring: 3 },
  { x: -140, y: 0, Icon: ArticleOutlined, ring: 3 },
  { x: -66, y: 30, Icon: ScienceOutlined, ring: 2 },
  { x: 84, y: 46, Icon: MedicalInformationOutlined, ring: 3 },
  { x: -112, y: 64, Icon: InsertDriveFileOutlined, ring: 3 },
];

const RINGS = [52, 88, 126, 166];

/**
 * @param {React.ReactNode} icon - merkezdeki kutucuğun ikonu (bağlam: hasta, takvim, …)
 * @param {"md"|"sm"} [size="md"]
 */
export default function EmptyIllustration({ icon, size = "md" }) {
  const s = size === "sm" ? 0.72 : 1;
  const W = 360 * s;
  const H = 190 * s;
  const cx = W / 2;
  const cy = 104 * s;

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: W,
        maxWidth: "100%",
        height: H,
        // Kenarlara doğru eriyen halkalar (alt kenar başlığa yer açar)
        maskImage: "radial-gradient(ellipse 60% 62% at 50% 55%, #000 55%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(ellipse 60% 62% at 50% 55%, #000 55%, transparent 100%)",
      }}
    >
      <svg width={W} height={H} style={{ position: "absolute", inset: 0 }}>
        {RINGS.map((r) => (
          <circle key={r} cx={cx} cy={cy} r={r * s} fill="none" stroke={COLORS.slate200} strokeWidth={1} />
        ))}
      </svg>

      {TILES.map(({ x, y, Icon, ring }, i) => {
        const tile = (ring === 2 ? 36 : 30) * s;
        return (
          <Box
            key={i}
            sx={{
              position: "absolute",
              left: cx + x * s,
              top: cy + y * s,
              width: tile,
              height: tile,
              transform: "translate(-50%, -50%)",
              borderRadius: `${tile * 0.28}px`,
              bgcolor: COLORS.white,
              border: `1px solid ${COLORS.slate200}`,
              boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: COLORS.slate400,
              opacity: ring === 2 ? 0.95 : 0.6,
              animation: `${float} ${5 + (i % 3)}s ease-in-out ${i * 0.4}s infinite`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Icon sx={{ fontSize: tile * 0.52 }} />
          </Box>
        );
      })}

      {/* Merkez kutucuk */}
      <Box
        sx={{
          position: "absolute",
          left: cx,
          top: cy,
          width: 60 * s,
          height: 60 * s,
          transform: "translate(-50%, -50%)",
          borderRadius: `${16 * s}px`,
          bgcolor: COLORS.white,
          border: `1px solid ${COLORS.slate200}`,
          boxShadow: `0 0 0 6px ${alpha(COLORS.slate50, 0.9)}, 0 10px 24px rgba(15,23,42,0.12)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: COLORS.slate500,
          "& svg": { fontSize: 28 * s },
        }}
      >
        {icon || <FolderOpenOutlined />}
      </Box>
    </Box>
  );
}
